/**
 * File Name          : physens-ble-device.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { BleClient } from "@capacitor-community/bluetooth-le"
import { Capacitor } from "@capacitor/core"
import { BehaviorSubject, Subject } from "rxjs"
import { HexStringUtils } from "../../utilities/hex-string-utils"
import { McumgrCap } from "../../utilities/mcumgr-cap"
import { PhysensDevice, Device_State, OTA_DIAG } from "./physens-device.model"
import { ACCESS_CONTROL_ROLE } from "../file_system/access-control-role.model"
import { GATT_UUID } from "../uuid/gatt-uuid.model"

// Dev-only test switch: when true, forces connect()'s mid-upload (updateInProgress, not
// expectingReboot) attempts to behave as a wedged/stuck Chrome connect queue for the first two
// attempts, so the stuck-reconnect screen + 2-strikes teardown can be exercised on demand instead
// of needing a real wedged ChromeOS queue. MUST stay false for shipping builds — list this
// alongside OTA_DIAG (physens-device.model.ts) and FORCE_UPLOAD_FOR_CONFIRM_TEST
// (firmwareupdate.component.ts) as flags to double-check/remove before shipping.
const OTA_SIMULATE_STUCK_RECONNECT = false;

export class PhysensBleDevice extends PhysensDevice {
    mcumgr: McumgrCap;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reselectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectInProgress = false;
  private hungConnectRetries = 0;
  private static readonly MAX_HUNG_CONNECT_RETRIES = 3;
  private reconnectDelayMs = 3000;
  private readonly maxReconnectDelayMs = 15000;
  private disconnectedAtMs: number | null = null;
  // Timestamp of the last time WE deliberately tore down / abandoned an in-flight
  // BleClient.connect() (OTA clean-reconnect, stale-session cleanup, or the hung-connect
  // queue reset). Used only by the DIAG connect-failure log to attribute an AbortError to
  // our own cancel/retry loop rather than a genuine link failure.
  private lastSelfCancelAt: number | null = null;
  // Public (not just for internal bookkeeping) so firmwareupdate.component's OTA state
  // machine can log which reconnect path actually recovered the device — part of the
  // state-transition log required for the OTA update flow (RECONNECTING step).
  reconnectMethod: 'watchAdvertisements' | 'plain connect' | 'manual reselect' | null = null;
  // Plain (non-OTA) disconnects — e.g. FW 99.6.0 dropping BLE ~5s after a recorder
  // window ends — deserve a longer runway for the automatic watchAdvertisements()/
  // backoff reconnect to recover before bothering the user, since a mid-lesson
  // reselect prompt is disruptive. Note this only governs this path: firmware-update
  // reconnects are gated separately by updateInProgress and have their own
  // independent 45s/120s timeouts in firmwareupdate.component's waitForDeviceReboot().
  // (This was briefly 15s in an OTA-chromebook fix, on the reasoning that ChromeOS's
  // auto-reconnect essentially never recovers on its own after an OTA reboot and a
  // 60s wait felt stuck — extended back out here since that OTA case doesn't hit this
  // path at all.)
  private readonly reselectThresholdMs = 75_000;
  reconnectRequiresUserReselect = new BehaviorSubject<boolean>(false);
  // Fires on every reconnect attempt that fails with Web Bluetooth's "Bluetooth Device
  // is no longer in range" NetworkError specifically — on ChromeOS this is what a stale
  // BluetoothDevice reference looks like once the physical device has rotated its BLE
  // address (e.g. across an OTA reboot), and no amount of further plain-connect retrying
  // against that same stale reference will ever succeed; only a fresh requestDevice()
  // chooser scan (a new GATT lookup, not a cached handle) can find it again. Unlike
  // reconnectRequiresUserReselect, this is NOT gated by updateInProgress — callers that
  // care about a real OTA reboot (firmwareupdate.component's waitForDeviceReboot) need
  // this signal specifically DURING an update, to stop waiting out a generic soft
  // timeout once it's clear the automatic loop is structurally unable to recover.
  staleAddressReconnectFailureSubject = new Subject<void>();
  // True once a disconnect was explicitly requested (Disconnect button, charging/inactivity
  // auto-disconnect, device removal) rather than an unexpected link loss. While true,
  // scheduleReconnect()/updateDeviceState() must never start a reconnect — only a fresh
  // user-initiated connect() (i.e. called with maxAttempts, as the manual reselect/retry
  // paths do) clears it.
  private userInitiatedDisconnect = false;
  // Paused by the OTA verify flow (firmwareupdate.component) the instant the 90s hard
  // reconnect timeout fires: it stops the automatic reconnect loop AND abandons any pending
  // BleClient.connect() so a background attempt can't race the user's manual "Reconnect to
  // Verify" chooser. Stays paused until the user taps reconnect (resumeAutoReconnectForVerify).
  private autoReconnectPausedForVerify = false;
  // Tears down the in-flight connect()'s outer timeout + heartbeat and marks it settled so its
  // late resolve/reject is ignored and its retry timer never fires. Set while a
  // BleClient.connect() is pending, nulled the moment it settles.
  private pendingConnectCleanup: (() => void) | null = null;
  // Counts reconnect attempts that have hung/rejected during an ACTIVE mid-upload OTA
  // (updateInProgress && !expectingReboot && !otaVerifying — never during the expected
  // post-reset reboot window, which owns its own recovery via
  // attemptPostRebootReconnect()/tap-to-finish, never during the post-reconnect read-only
  // verify window, and never for a plain non-OTA disconnect, which keeps using
  // hungConnectRetries/MAX_HUNG_CONNECT_RETRIES unchanged). Reset to 0 on any successful
  // reconnect. At 2, the loop stops and teardownStuckOtaConnection() runs instead of
  // retrying again.
  private otaReconnectStuckFailures = 0;
  // Fires once teardownStuckOtaConnection() has run — firmwareupdate.component shows its own
  // "Connection lost. Tap to reconnect" screen off this (NOT reconnectRequiresUserReselect,
  // which showSelectDevicePopup() deliberately keeps hidden for the whole OTA so it can't race
  // this screen or the tap-to-finish/verify flow).
  otaReconnectStuck = new Subject<void>();

    constructor(deviceId: string, deviceName?: string) {
        super(deviceId, deviceName, {pollFileRecorderState: true})
        this.mcumgr = new McumgrCap(this)
    }

    private clearReconnectTimer() {
      if (this.reconnectTimer) {
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] clearing pending reconnect timer`)
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    }

    private clearReselectTimer() {
      if (this.reselectTimer) {
        clearTimeout(this.reselectTimer)
        this.reselectTimer = null
      }
    }

    // Fires reconnectRequiresUserReselect exactly reselectThresholdMs after the device
    // was first marked DISCONNECTED, independent of in-flight BleClient.connect() attempts
    // — those can each take 10-20s to reject with NetworkError when the device is out of
    // range, so relying on the connect-failure callback to notice the threshold crossing
    // was pushing the popup out to ~90-120s instead of 75s.
    private armReselectTimer() {
      if (this.reselectTimer || this.disconnectedAtMs === null) return
      if (Capacitor.getPlatform() !== 'web' || this.expectingReboot) return
      const elapsedMs = Date.now() - this.disconnectedAtMs
      const remainingMs = Math.max(0, this.reselectThresholdMs - elapsedMs)
      console.debug(`[BLE][${this.deviceName ?? this.deviceId}] arming reselect timer in ${remainingMs}ms (elapsed ${elapsedMs}ms / threshold ${this.reselectThresholdMs}ms)`)
      this.reselectTimer = setTimeout(() => {
        this.reselectTimer = null
        if (this.removed || this.expectingReboot) return
        if (this.deviceState === Device_State.READY_FOR_PACKETS) return
        if (this.reconnectRequiresUserReselect.value) return
        console.warn(`[BLE][${this.deviceName ?? this.deviceId}] reselect threshold reached — prompting user to reselect`)
        this.reconnectRequiresUserReselect.next(true)
      }, remainingMs)
    }

    public cancelAutoReconnect() {
      this.clearReconnectTimer()
      this.reconnectDelayMs = 3000
      console.debug(`[BLE][${this.deviceName ?? this.deviceId}] auto-reconnect cancelled and backoff reset`)
    }

    public get autoReconnectPaused(): boolean {
      return this.autoReconnectPausedForVerify
    }

    // Called by the OTA verify flow when the 90s hard reconnect timeout fires. Stops the
    // automatic reconnect loop and abandons any pending BleClient.connect() (so a background
    // attempt can't paint over / race the user's manual chooser), and keeps the loop stopped
    // until resumeAutoReconnectForVerify().
    public pauseAutoReconnectForVerify() {
      this.autoReconnectPausedForVerify = true
      this.clearReconnectTimer()
      this.lastSelfCancelAt = Date.now()
      if (this.pendingConnectCleanup) {
        this.pendingConnectCleanup()
        this.pendingConnectCleanup = null
      }
      BleClient.enableQueue()
      this.connectInProgress = false
      console.log(`[BLE][${this.deviceName ?? this.deviceId}] auto-reconnect paused for verify; pending connect aborted`)
    }

    // Re-enables the automatic reconnect loop once the user has initiated the manual chooser
    // reconnect in the verify flow.
    public resumeAutoReconnectForVerify() {
      if (!this.autoReconnectPausedForVerify) return
      this.autoReconnectPausedForVerify = false
      console.log(`[BLE][${this.deviceName ?? this.deviceId}] auto-reconnect resumed for verify`)
    }

    // Called by the OTA flow the instant an update completes successfully AND the device is
    // confirmed physically back (the measured ~61s reboot point). Every reconnect attempt the
    // background loop made before now fired while the device was still mid-reboot - its GATT
    // server wasn't up yet, so each one wedged behind BleClient's serial queue and timed out.
    // Clear that wedged state and issue exactly ONE clean reconnect now, at the first moment the
    // device is actually connectable. If this still can't reach it (e.g. ChromeOS rotated the
    // device's BLE address across the reboot, leaving the stored handle unusable), the
    // connect-failure path arms the reselect flow and the "Reconnect Device" prompt appears so
    // the user re-pairs with a single tap via the chooser - the only reliable recovery there.
    public reconnectAfterOtaSuccess() {
      this.userInitiatedDisconnect = false
      this.clearReconnectTimer()
      // Abandon any connect still wedged in BleClient's FIFO queue from a mid-reboot attempt,
      // then clear our own in-flight flag so the fresh connect() below isn't skipped (connect()
      // early-returns while connectInProgress is true).
      BleClient.enableQueue()
      this.connectInProgress = false
      this.hungConnectRetries = 0
      this.resetConnectionAttempts()
      this.lastSelfCancelAt = Date.now()
      if (this.disconnectedAtMs === null) this.disconnectedAtMs = Date.now()
      console.log(`[FWUpdate] OTA success: issuing clean post-reboot reconnect for ${this.deviceName ?? this.deviceId}`)
      this.cleanupStaleConnection()
        .then(() => { this.connect(4) })
        .catch((e) => console.warn(`[FWUpdate] OTA success: post-reboot reconnect kickoff failed for ${this.deviceName ?? this.deviceId}`, e))
    }

    // Best-effort, non-blocking reconnect attempt after an OTA update has already been
    // confirmed and declared successful (see the firmware-update component's
    // rebootAfterConfirm()). Unlike reconnectAfterOtaSuccess(), this must NEVER surface an
    // error or arm the manual-reselect prompt — success is already on screen, so a failed or
    // unsupported reconnect here is simply logged and swallowed. Reuses
    // tryWatchAdvertisementReconnect() (web-only, event-driven) since it already falls through
    // cleanly on any unsupported/error/timeout condition instead of throwing.
    public async attemptSilentPostOtaReconnect(): Promise<void> {
      try {
        const reconnected = await this.tryWatchAdvertisementReconnect()
        if (reconnected) {
          console.log(`[FWUpdate] post-OTA silent reconnect succeeded for ${this.deviceName ?? this.deviceId}`)
          try {
            const imageState = await this.mcumgr?.cmdImageState()
            const active = imageState?.images.find((i: any) => i.active) ?? imageState?.images?.[0]
            console.log(`[FWUpdate] post-OTA silent reconnect: active image version=${active?.version} confirmed=${active?.confirmed}`)
          } catch (e) {
            console.debug('[FWUpdate] post-OTA silent reconnect: image-state read failed (non-fatal)', e)
          }
        } else {
          console.log(`[FWUpdate] post-OTA silent reconnect unavailable/failed (non-fatal) for ${this.deviceName ?? this.deviceId}`)
        }
      } catch (e) {
        console.debug('[FWUpdate] post-OTA silent reconnect errored (non-fatal)', e)
      }
    }

    // Resolves once deviceState reaches target, or false after timeoutMs. Used by the
    // post-reboot raw-GATT reconnect paths below to know when a handed-off link has actually
    // made it through setupNotifications() to READY_FOR_PACKETS, since connect() itself only
    // reports that BleClient.connect() resolved, not that the packet handshake finished.
    private waitForState(target: Device_State, timeoutMs: number): Promise<boolean> {
      if (this.deviceState === target) return Promise.resolve(true)
      return new Promise((resolve) => {
        let settled = false
        const timer = setTimeout(() => {
          if (settled) return
          settled = true
          sub.unsubscribe()
          resolve(false)
        }, timeoutMs)
        const sub = this.deviceStateSubject.subscribe((s) => {
          if (settled || s !== target) return
          settled = true
          clearTimeout(timer)
          sub.unsubscribe()
          resolve(true)
        })
      })
    }

    // Shared tail end of every raw-GATT reconnect path (the post-reboot poll loop below, and
    // the manual tap-to-finish requestDevice() path driven by the component): given a
    // BluetoothDevice object (from getDevices() or requestDevice()), tears down any stale gatt
    // session, connects raw gatt with its own short timeout (so a non-connectable device fails
    // fast instead of wedging BleClient's 20s/hung-retry machinery), adopts the device's current
    // id if it changed across the reboot (logged loudly — this is the proof point for whether
    // ChromeOS rotated the BLE address), then hands the now-connected link to BleClient
    // (refreshWebDeviceHandle() repopulates its internal device map so its own connect() finds
    // an already-connected gatt and resolves near-instantly instead of racing a fresh
    // gatt.connect()) and waits for the normal setupNotifications()->READY_FOR_PACKETS flow.
    private async connectRawDeviceAndHandoff(rawDevice: any, gattTimeoutMs: number, readyTimeoutMs: number): Promise<boolean> {
      const expectedRawId = HexStringUtils.hexToString(this.deviceId)
      if (rawDevice.id !== expectedRawId) {
        console.warn(`[FWUpdate] post-reboot reconnect: granted device id changed across reboot ${expectedRawId} -> ${rawDevice.id} (matched by name=${rawDevice.name ?? '?'}) — adopting new id`)
        this.deviceId = HexStringUtils.stringToHex(rawDevice.id)
      } else {
        console.log(`[FWUpdate] post-reboot reconnect: device id unchanged across reboot (${expectedRawId})`)
      }
      try {
        if (rawDevice.gatt?.connected) {
          console.debug(`[BLE][${this.deviceName ?? this.deviceId}] post-reboot: stale gatt session detected, disconnecting first`)
          try { rawDevice.gatt.disconnect() } catch {}
        }
        console.log(`[FWUpdate] post-reboot reconnect: raw gatt.connect() starting (timeout ${gattTimeoutMs}ms)`)
        await Promise.race([
          rawDevice.gatt.connect(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('raw gatt.connect timeout')), gattTimeoutMs)),
        ])
        console.log(`[FWUpdate] post-reboot reconnect: raw gatt.connect() SUCCEEDED for ${this.deviceName ?? this.deviceId}`)
      } catch (e) {
        console.warn(`[FWUpdate] post-reboot reconnect: raw gatt.connect() failed/timed out`, e)
        try { rawDevice.gatt?.disconnect() } catch {}
        return false
      }
      await this.refreshWebDeviceHandle()
      this.reconnectMethod = 'plain connect'
      this.hungConnectRetries = 0
      this.connectInProgress = false
      const started = this.connect(4)
      if (!started) {
        console.warn(`[FWUpdate] post-reboot reconnect: handoff connect() declined to start for ${this.deviceName ?? this.deviceId}`)
        return false
      }
      const ready = await this.waitForState(Device_State.READY_FOR_PACKETS, readyTimeoutMs)
      if (!ready) console.warn(`[FWUpdate] post-reboot reconnect: handed-off link did not reach READY_FOR_PACKETS within ${readyTimeoutMs}ms`)
      return ready
    }

    // Owns the entire post-OTA-reset reconnect window on web, replacing reliance on
    // BleClient.connect()'s own 20s-timeout/hung-retry machinery (which on ChromeOS wedges
    // indefinitely against a device that isn't GATT-connectable yet, right after a reboot).
    // Phase 1 (quietMs, ~65s): deliberately does nothing — no BleClient.connect() / raw
    // gatt.connect() of any kind runs here, and scheduleReconnect() is already suppressed the
    // whole time via the expectingReboot check above — this always runs first, REGARDLESS of
    // whether this platform even has getDevices, so the caller's progress UI can ramp for the
    // full window before any reconnect decision is made. Phase 2 (windowMs): only reachable on
    // platforms that expose navigator.bluetooth.getDevices() (NOT ChromeOS — confirmed via field
    // diagnostics that getDevices is undefined there); polls it every intervalMs and hands any
    // match off to connectRawDeviceAndHandoff(). On a platform without getDevices, returns false
    // immediately after the quiet period so the caller shows the native tap-to-finish button —
    // the only route to reconnect left on that platform. Returns true only once READY_FOR_PACKETS
    // is reached; false if there's no getDevices, or the whole active window elapses first.
    async attemptPostRebootReconnect(quietMs = 65000, windowMs = 120000, intervalMs = 4000, gattTimeoutMs = 8000): Promise<boolean> {
      if (Capacitor.getPlatform() !== 'web') return false

      console.log(`[FWUpdate] state=REBOOTING: quiet period starting — no connect attempts for ${quietMs}ms while DataSnap finishes booting`)
      await new Promise((r) => setTimeout(r, quietMs))
      if (this.removed || this.userInitiatedDisconnect) return false

      const bluetooth = (typeof navigator !== 'undefined') ? (navigator as any).bluetooth : undefined
      if (!bluetooth?.getDevices) {
        console.log(`[FWUpdate] state=REBOOTING: navigator.bluetooth.getDevices() unavailable on this platform — no silent reconnect possible, surfacing tap-to-finish`)
        return false
      }

      const expectedRawId = HexStringUtils.hexToString(this.deviceId)
      const cachedName = this.deviceName
      const supportsWatchAdvertisements = !!(globalThis as any).BluetoothDevice?.prototype?.watchAdvertisements
      const deadline = Date.now() + windowMs
      let attempt = 0

      while (Date.now() < deadline) {
        attempt++
        if (this.removed || this.userInitiatedDisconnect) return false
        let devices: any[] = []
        try {
          devices = await bluetooth.getDevices()
        } catch (e) {
          console.warn(`[FWUpdate] post-reboot reconnect attempt ${attempt}: getDevices() failed`, e)
        }
        const idsAndNames = devices.map((d) => `${d.name ?? '?'}/${d.id}`).join(', ') || '(none)'
        const byId = devices.find((d) => d.id === expectedRawId)
        const byName = cachedName ? devices.find((d) => d.name === cachedName) : undefined
        if (OTA_DIAG) {
          console.log(`[FWUpdate][DIAG] post-reboot getDevices() attempt ${attempt} -> [${idsAndNames}]; cachedId=${expectedRawId} cachedName=${cachedName ?? '?'}; idMatch=${byId ? 'YES' : 'NO'} nameMatch=${byName ? 'YES' : 'NO'}${byId && byName && byId !== byName ? ' (id-match and name-match are DIFFERENT objects)' : ''}`)
        }
        const matched = byId ?? byName
        if (matched) {
          const reconnected = await this.connectRawDeviceAndHandoff(matched, gattTimeoutMs, 15000)
          if (reconnected) return true
        } else {
          console.log(`[FWUpdate] post-reboot reconnect attempt ${attempt}: device not yet found in getDevices()`)
        }

        const remainingMs = deadline - Date.now()
        if (remainingMs <= 0) break
        const waitMs = Math.min(intervalMs, remainingMs)
        if (supportsWatchAdvertisements && matched) {
          // Device was found but not yet connectable (or handoff failed) — race the next
          // poll tick against an advertisement event so a device that becomes connectable
          // mid-interval doesn't wait out the full tick.
          await new Promise<void>((resolve) => {
            let settled = false
            const timer = setTimeout(() => { if(settled) return; settled = true; resolve() }, waitMs)
            const onAdv = () => { if(settled) return; settled = true; clearTimeout(timer); matched.removeEventListener('advertisementreceived', onAdv); resolve() }
            matched.addEventListener('advertisementreceived', onAdv)
            matched.watchAdvertisements?.().catch(() => undefined)
            setTimeout(() => matched.removeEventListener('advertisementreceived', onAdv), waitMs + 50)
          })
        } else {
          await new Promise((r) => setTimeout(r, waitMs))
        }
      }
      console.log(`[FWUpdate] state=REBOOTING: post-reboot reconnect loop gave up after ${attempt} attempts within ${windowMs}ms active window`)
      return false
    }

    // Driven by the component's "Tap to finish" button once attemptPostRebootReconnect()'s
    // quiet window has elapsed without an automatic reconnect — the only remaining route on a
    // platform without navigator.bluetooth.getDevices (confirmed via field diagnostics to be
    // ChromeOS). The very first statement below must call BleClient.requestDevice() with no
    // preceding await — Chrome/ChromeOS only paints the native chooser when requestDevice() runs
    // synchronously inside the click's own task. Deliberately uses BleClient.requestDevice()
    // (NOT a raw navigator.bluetooth.requestDevice() call) so the granted device is already known
    // to BleClient's own internal device map — this is the exact same chooser call the initial
    // pairing flow uses (idl-physens.service.ts's scanForDevice()), which is why the subsequent
    // BleClient.connect() below can resolve in well under a second instead of the raw
    // gatt.connect() this used to attempt first, which field logs show fails with a ChromeOS
    // "NetworkError: Unknown error when connecting to the device" even moments after the exact
    // same device was granted. Retries BleClient.connect() itself up to 3 times with a 3s gap —
    // never surfaces a hard error or a fake success; a full exhaustion just returns false so the
    // caller can re-arm the same button.
    async finishManualTapReconnect(): Promise<boolean> {
      const name = this.deviceName
      console.log(`[FWUpdate] tap-to-finish: calling BleClient.requestDevice({name:"${name ?? '?'}"}) synchronously on click`)
      let granted: { deviceId: string; name?: string }
      try {
        granted = await BleClient.requestDevice(name ? { name } : {})
      } catch (e) {
        console.warn(`[FWUpdate] tap-to-finish: requestDevice() rejected/dismissed`, e)
        return false
      }
      console.log(`[FWUpdate] tap-to-finish: chooser resolved deviceId=${granted.deviceId} name=${granted.name ?? '?'}`)
      const expectedRawId = HexStringUtils.hexToString(this.deviceId)
      if (granted.deviceId !== expectedRawId) {
        console.warn(`[FWUpdate] tap-to-finish: granted device id changed across reboot ${expectedRawId} -> ${granted.deviceId} — adopting new id`)
        this.deviceId = HexStringUtils.stringToHex(granted.deviceId)
      } else {
        console.log(`[FWUpdate] tap-to-finish: device id unchanged across reboot (${expectedRawId})`)
      }

      this.reconnectMethod = 'manual reselect'
      const connectOptions = Capacitor.getPlatform() === 'web' ? { timeout: 20000 } : {}
      for (let attempt = 1; attempt <= 3; attempt++) {
        console.log(`[FWUpdate] tap-to-finish: BleClient.connect() attempt ${attempt}/3`)
        try {
          await BleClient.connect(HexStringUtils.hexToString(this.deviceId), () => this.updateDeviceState(Device_State.DISCONNECTED), connectOptions)
          console.log(`[FWUpdate] tap-to-finish: BleClient.connect() succeeded on attempt ${attempt}/3`)
          this.hungConnectRetries = 0
          this.otaReconnectStuckFailures = 0
          this.connectInProgress = false
          this.hasConnected = true
          this.disconnectedAtMs = null
          this.clearReselectTimer()
          this.reconnectRequiresUserReselect.next(false)
          this.updateDeviceState(Device_State.CONNECTED)
          const ready = await this.waitForState(Device_State.READY_FOR_PACKETS, 15000)
          if (ready) return true
          console.warn(`[FWUpdate] tap-to-finish: connected but never reached READY_FOR_PACKETS (attempt ${attempt}/3)`)
        } catch (e) {
          console.warn(`[FWUpdate] tap-to-finish: BleClient.connect() attempt ${attempt}/3 failed`, e)
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, 3000))
      }
      console.warn(`[FWUpdate] tap-to-finish: all 3 BleClient.connect() attempts failed for ${this.deviceName ?? this.deviceId}`)
      return false
    }

    /**
     * Disconnects any stale/half-open GATT session before reconnecting. Required before
     * every reconnect attempt (automatic backoff or a manual user reselect) — issuing
     * BleClient.connect() on top of a stale session silently times out instead of
     * establishing a fresh connection.
     */
    async cleanupStaleConnection() {
      this.hungConnectRetries = 0
      this.otaReconnectStuckFailures = 0
      this.lastSelfCancelAt = Date.now()
      try {
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] cleanup disconnect before reconnect`)
        await BleClient.disconnect(HexStringUtils.hexToString(this.deviceId))
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] cleanup disconnect succeeded`)
      } catch {
        // Ignore: Web Bluetooth may already consider the device disconnected.
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] cleanup disconnect skipped (already disconnected)`)
      }
      await this.refreshWebDeviceHandle()
    }

    // Runs once the mid-upload OTA reconnect loop has failed/hung twice in a row (see the outer-
    // timeout and rejection branches of connect() below). Each step is independently try/catch'd
    // with its own short timeout so a wedged BleClient.disconnect() call can't make teardown
    // itself hang — the whole point is to guarantee this always completes and reaches
    // otaReconnectStuck.next() so the UI can show the manual tap-to-reconnect screen. Order
    // matches the spec: disconnect, clear writePromise, clear mcumgr's pending resolvers, then
    // reset the BleClient plugin queue.
    private async teardownStuckOtaConnection(): Promise<void> {
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG teardown: starting for ${this.deviceName ?? this.deviceId}`)
      try {
        await Promise.race([
          BleClient.disconnect(HexStringUtils.hexToString(this.deviceId)),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('teardown disconnect timeout')), 3000)),
        ])
        if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG teardown step: BleClient.disconnect() done for ${this.deviceName ?? this.deviceId}`)
      } catch (e) {
        if (OTA_DIAG) console.warn(`[FWUpdate][DIAG] OTA_DIAG teardown step: BleClient.disconnect() failed/timed out (non-fatal)`, e)
      }
      try {
        this.writePromise = null
        if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG teardown step: writePromise cleared for ${this.deviceName ?? this.deviceId}`)
      } catch (e) {
        console.warn(`[FWUpdate][DIAG] OTA_DIAG teardown step: clearing writePromise failed (non-fatal)`, e)
      }
      try {
        this.mcumgr?.clearPendingResolvers('OTA reconnect stuck')
        if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG teardown step: mcumgr.clearPendingResolvers() done for ${this.deviceName ?? this.deviceId}`)
      } catch (e) {
        console.warn(`[FWUpdate][DIAG] OTA_DIAG teardown step: mcumgr.clearPendingResolvers() failed (non-fatal)`, e)
      }
      try {
        BleClient.enableQueue()
        if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG teardown step: BleClient.enableQueue() (queue reset) done for ${this.deviceName ?? this.deviceId}`)
      } catch (e) {
        console.warn(`[FWUpdate][DIAG] OTA_DIAG teardown step: BleClient.enableQueue() failed (non-fatal)`, e)
      }
      this.connectInProgress = false
      this.hungConnectRetries = 0
      console.error(`[FWUpdate] state=RECONNECTING (OTA): mid-upload reconnect loop stopped after ${this.otaReconnectStuckFailures} failures for ${this.deviceName ?? this.deviceId} — showing manual tap-to-reconnect screen`)
      this.otaReconnectStuck.next()
    }

    /**
     * On ChromeOS, a plain BleClient.connect() by cached deviceId can fail forever
     * with "no longer in range" after the physical device reconnects post-OTA-reboot
     * (e.g. once its BLE address rotates) - yet a manual reselect, which re-fetches
     * the device via navigator.bluetooth.getDevices()/requestDevice(), recovers
     * instantly. BleClient.getDevices() performs that same navigator.bluetooth.getDevices()
     * lookup and re-registers the result in the plugin's internal device map (web.js),
     * overwriting whatever stale BluetoothDevice reference it was holding - and unlike
     * requestDevice(), getDevices() needs no user gesture/chooser. Running this before
     * every automatic reconnect attempt gives the plain-connect path the same fresh
     * handle the manual button gets, without requiring the user to click anything.
     */
    private async refreshWebDeviceHandle() {
      if(Capacitor.getPlatform() !== 'web') return
      const bluetooth = (typeof navigator !== 'undefined') ? (navigator as any).bluetooth : undefined
      if(!bluetooth?.getDevices) return
      try {
        await BleClient.getDevices([HexStringUtils.hexToString(this.deviceId)])
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] refreshed device handle via getDevices()`)
      } catch (e) {
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] getDevices() refresh failed (non-fatal)`, e)
      }
    }

    // Stops the background retry loop once we've already told the user they must
    // reselect via the chooser — otherwise a background attempt can race the manual
    // reselect flow and silently reconnect (or fail) on its own, clearing/leaving
    // reconnectRequiresUserReselect out of sync with whatever the user just did in
    // the chooser (e.g. cancelling it) and yanking the "Reconnect Device" popup away.
    private scheduleReconnect() {
      if (this.removed || this.userInitiatedDisconnect || this.connectInProgress || this.reconnectTimer || this.reconnectRequiresUserReselect.value || this.autoReconnectPausedForVerify || this.expectingReboot || (this.updateInProgress && this.otaReconnectStuckFailures >= 2)) {
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] reconnect not scheduled`, {
          removed: this.removed,
          userInitiatedDisconnect: this.userInitiatedDisconnect,
          connectInProgress: this.connectInProgress,
          timerPending: this.reconnectTimer != null,
          requiresUserReselect: this.reconnectRequiresUserReselect.value,
          pausedForVerify: this.autoReconnectPausedForVerify,
          expectingReboot: this.expectingReboot,
          otaReconnectStuck: this.updateInProgress && this.otaReconnectStuckFailures >= 2,
        })
        return
      }

      // watchAdvertisements() is event-driven (it resolves the instant the device
      // reappears) rather than a blind poll, so unlike the plain-connect fallback it
      // doesn't need the same backoff delay to avoid spamming the device - imposing it
      // anyway was pure dead time before the listener was even registered, and was the
      // root cause of Chrome's reconnect landing several seconds behind Windows/native
      // (which skips this delay entirely, since tryWatchAdvertisementReconnect() is a
      // no-op off the web platform). Start listening immediately on web; keep the
      // backoff for native platforms, which only ever take the plain-connect path.
      const platform = Capacitor.getPlatform()
      const delayMs = platform === 'web' ? 0 : this.reconnectDelayMs
      console.log(`[BLE][${this.deviceName ?? this.deviceId}] reconnect scheduled in ${delayMs}ms`)

      this.reconnectTimer = setTimeout(async () => {
        this.reconnectTimer = null
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] reconnect timer fired`)
        console.log(`[FWUpdate] reconnect attempt starting for ${this.deviceName ?? this.deviceId}`)
        const reconnectedViaAdvertisement = await this.tryWatchAdvertisementReconnect()
        if(!reconnectedViaAdvertisement) {
          console.log('[FWUpdate] reconnect method: plain connect (watchAdvertisements unavailable/failed/not yet in range)')
          this.reconnectMethod = 'plain connect'
          await this.cleanupStaleConnection()
          this.connect()
        }
      }, delayMs)

      if(platform !== 'web') {
        this.reconnectDelayMs = Math.min(this.reconnectDelayMs + 2000, this.maxReconnectDelayMs)
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] next reconnect backoff set to ${this.reconnectDelayMs}ms`)
      }
    }

    /**
     * Web-only fast path for the post-OTA reconnect: rather than blindly polling
     * BleClient.connect() on a fixed backoff (which on ChromeOS can spend its whole
     * attempt window failing against a GATT server the device hasn't finished
     * re-advertising yet), ask the browser to tell us the instant the device is back
     * in range via watchAdvertisements() — the origin already holds permission from
     * the original requestDevice() chooser grant, so this needs no user gesture.
     * Falls through (returns false) on any unsupported/error/timeout condition so the
     * existing cleanupStaleConnection()+connect() path is unaffected.
     */
    private async tryWatchAdvertisementReconnect(timeoutMs = 12000): Promise<boolean> {
      if(Capacitor.getPlatform() !== 'web') return false
      const bluetooth = (typeof navigator !== 'undefined') ? (navigator as any).bluetooth : undefined
      if(!bluetooth?.getDevices || !(globalThis as any).BluetoothDevice?.prototype?.watchAdvertisements) {
        return false
      }
      // During an OTA the device reboots into new firmware and doesn't start
      // re-advertising until ~13-15s after the disconnect - past the default 12s
      // window. Timing out early here drops the reconnect onto the plain
      // BleClient.connect() fallback (scheduleReconnect), which on ChromeOS wedges
      // indefinitely against the stale GATT server while the device is still booting
      // ("still waiting on BleClient.connect()" - never resolves, never times out).
      // watchAdvertisements() is event-driven (it resolves the instant the device
      // reappears), so widening the window to comfortably cover the full reboot costs
      // nothing when the device is already back - it just keeps the post-OTA reconnect
      // on this working path instead of the hanging one, so a real READY_FOR_PACKETS
      // can fire and let the progress bar complete only once the device is genuinely back.
      const effectiveTimeoutMs = this.expectingReboot ? 40000 : timeoutMs
      const rawDeviceId = HexStringUtils.hexToString(this.deviceId)
      try {
        console.log(`[FWUpdate] reconnect method: watchAdvertisements - looking up granted device ${this.deviceName ?? this.deviceId}`)
        const devices: any[] = await bluetooth.getDevices()
        const device = devices.find((d) => d.id === rawDeviceId)
        if(!device) {
          console.log('[FWUpdate] watchAdvertisements skipped - device not found in navigator.bluetooth.getDevices()')
          return false
        }
        const gotAdvertisement = await new Promise<boolean>((resolve) => {
          let settled = false
          const onAdvertisement = () => {
            if(settled) return
            settled = true
            clearTimeout(timer)
            device.removeEventListener('advertisementreceived', onAdvertisement)
            console.debug(`[BLE][${this.deviceName ?? this.deviceId}] advertisementreceived listener removed (count=0)`)
            resolve(true)
          }
          const timer = setTimeout(() => {
            if(settled) return
            settled = true
            device.removeEventListener('advertisementreceived', onAdvertisement)
            console.debug(`[BLE][${this.deviceName ?? this.deviceId}] advertisementreceived listener removed (count=0, timed out)`)
            resolve(false)
          }, effectiveTimeoutMs)
          device.addEventListener('advertisementreceived', onAdvertisement)
          console.debug(`[BLE][${this.deviceName ?? this.deviceId}] advertisementreceived listener added (count=1)`)
          device.watchAdvertisements().catch((e: any) => {
            console.warn(`[BLE][${this.deviceName ?? this.deviceId}] watchAdvertisements() call failed`, e)
            if(settled) return
            settled = true
            clearTimeout(timer)
            device.removeEventListener('advertisementreceived', onAdvertisement)
            resolve(false)
          })
        })
        if(!gotAdvertisement) {
          console.log('[FWUpdate] watchAdvertisements - no advertisement seen within', effectiveTimeoutMs, 'ms')
          return false
        }
        console.log('[FWUpdate] watchAdvertisements - advertisement received, device back in range, connecting')
        this.reconnectMethod = 'watchAdvertisements'
        await this.cleanupStaleConnection()
        return this.connect()
      } catch (e) {
        console.warn(`[BLE][${this.deviceName ?? this.deviceId}] watchAdvertisements reconnect path failed`, e)
        return false
      }
    }

    override connect(maxAttempts?: number): boolean {
      if (this.connectInProgress || this.removed) {
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect skipped`, {
          removed: this.removed,
          connectInProgress: this.connectInProgress,
        })
        return false;
      }

      if(super.connect(maxAttempts)) {
        this.clearReconnectTimer()
        this.connectInProgress = true
        console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect start`)
        // A maxAttempts is only ever passed by the manual "reconnect"/"reselect" retry
        // path (see DatasnapDevice.retryConnection); scheduleReconnect's automatic
        // attempts always call connect() with no arguments. Treat it as a fresh
        // user-initiated connect, clearing any earlier user-disconnect state so
        // reconnect logic can resume normally for this device.
        if (maxAttempts !== undefined) {
          this.userInitiatedDisconnect = false
        }
        if (maxAttempts !== undefined && this.disconnectedAtMs !== null) {
          this.reconnectMethod = 'manual reselect'
        }

        // Web Bluetooth's default GATT connect timeout (bleClient.js DEFAULT_CONNECTION_TIMEOUT
        // = 10000ms) is too short right after an OTA reboot: the device can take longer than
        // that to finish re-advertising/become truly GATT-connectable, so every web reconnect
        // attempt in that window silently times out (web.js connect() calls gatt.disconnect()
        // and rejects with "Connection timeout") and disconnects again before ever reaching
        // setupNotifications()/READY_FOR_PACKETS - this is the root cause of Chrome repeatedly
        // failing to reconnect post-OTA while native platforms (which don't go through this
        // Web Bluetooth path) recover fine. Only extend it on web.
        const connectOptions = Capacitor.getPlatform() === 'web' ? { timeout: 20000 } : {}
        console.log(`[FWUpdate] state=RECONNECTING: GATT connect attempt starting for ${this.deviceName ?? this.deviceId} (timeout=${(connectOptions as any).timeout ?? 'plugin default'})`)

        // BleClient (bleClient.js) serializes EVERY plugin call — connect, disconnect,
        // getDevices, read, etc. — through one internal FIFO queue (queue.js), and the
        // 20s `connectOptions.timeout` above only wraps device.gatt.connect() itself
        // (web.js connect()); it does nothing for the onDisconnect-listener add/remove
        // that bleClient.js's connect() runs *before* reaching that race. If that listener
        // housekeeping (or any earlier queued job, e.g. a stale disconnect) never settles,
        // this connect() call's own job never starts, so its 20s timeout never fires and
        // the promise below hangs forever — which is why "GATT connect attempt starting"
        // logs with no success/failure ever following. Guard with our own outer timeout,
        // and on expiry reset BleClient's queue (enableQueue() swaps in a brand-new queue,
        // abandoning the dead job) so this call and every future BLE op aren't wedged
        // behind it forever, then retry a bounded number of times before giving up.
        // --- TEMPORARY DIAGNOSTIC INSTRUMENTATION ---
        // Testing hypothesis A (device re-advertises under a NEW BLE address after
        // reboot, so our cached deviceId can never connect) and whether the JS main
        // thread itself is alive while we wait. Call navigator.bluetooth.getDevices()
        // directly (NOT via BleClient/its queue) so a stuck BleClient queue can't also
        // block this diagnostic — if this hangs too, the browser's BT layer itself is
        // stalled, not just our queue.
        const expectedRawId = HexStringUtils.hexToString(this.deviceId)
        // --- Fix 2 investigation (log only, no behavior change) ---
        // BleClient.connect() always targets the CACHED deviceId captured at first pairing. If
        // the device rotated its BLE address across the OTA reboot this id no longer matches any
        // advertising device, so the getDevices() DIAG below will report match=NO and the name
        // present (if any) will differ — the signature of the stale-reference reconnect failure.
        if (this.disconnectedAtMs !== null && OTA_DIAG) {
          console.log(`[FWUpdate][DIAG] BleClient.connect() reusing CACHED deviceId=${expectedRawId} name=${this.deviceName ?? '?'} for post-disconnect/reboot reconnect — comparing against getDevices() below to detect an address/name change across the reboot`)
        }
        if (OTA_DIAG && Capacitor.getPlatform() === 'web' && (navigator as any).bluetooth?.getDevices) {
          (navigator as any).bluetooth.getDevices().then((devices: any[]) => {
            const ids = devices.map((d) => `${d.name ?? '?'}/${d.id}`).join(', ') || '(none)'
            const match = devices.find((d) => d.id === expectedRawId)
            console.log(`[FWUpdate][DIAG] navigator.bluetooth.getDevices() -> [${ids}]; expecting id=${expectedRawId}; match=${match ? 'YES' : 'NO - no granted device has this id'}`)
          }).catch((e: any) => console.warn('[FWUpdate][DIAG] navigator.bluetooth.getDevices() itself failed/hung-then-rejected', e))
        }
        let diagTicks = 0
        const diagHeartbeat = setInterval(() => {
          diagTicks++
          if (OTA_DIAG) console.log(`[FWUpdate][DIAG] heartbeat tick ${diagTicks} (${diagTicks * 2}s elapsed) — main thread alive, still waiting on BleClient.connect() for ${this.deviceName ?? this.deviceId}`)
          if (diagTicks >= 20) clearInterval(diagHeartbeat)
        }, 2000)
        // --- END DIAGNOSTIC INSTRUMENTATION ---

        // See OTA_SIMULATE_STUCK_RECONNECT's declaration above: dev-only, forces the next
        // two mid-upload attempts to behave like a wedged Chrome connect queue (hang until
        // the outer timeout) so the 2-strikes teardown + tap-to-reconnect screen can be
        // exercised without a real wedged ChromeOS queue. Never true for non-OTA or
        // post-reboot-window/verify connects, and never true once otaReconnectStuckFailures
        // reaches 2.
        const simulateStuckReconnect = OTA_SIMULATE_STUCK_RECONNECT && this.updateInProgress && !this.expectingReboot && !this.otaVerifying && this.otaReconnectStuckFailures < 2
        if (simulateStuckReconnect && OTA_DIAG) {
          console.log(`[FWUpdate][DIAG] OTA_DIAG OTA_SIMULATE_STUCK_RECONNECT active: forcing a fake hang for this attempt (failures so far=${this.otaReconnectStuckFailures})`)
        }
        const OUTER_TIMEOUT_MS = simulateStuckReconnect ? 500 : 25000
        let outerSettled = false
        const outerTimer = setTimeout(() => {
          if (outerSettled) return
          outerSettled = true
          clearInterval(diagHeartbeat)
          this.pendingConnectCleanup = null
          this.connectInProgress = false

          // Mid-upload OTA link-loss (updateInProgress && !expectingReboot && !otaVerifying —
          // never the expected post-reset reboot window or the post-reconnect verify window,
          // both of which keep using the unmodified logic below) gets its own isolated
          // 2-strikes counter and give-up path so a wedged Chrome connect queue during an
          // active upload can be torn down and handed to the user via a tap-to-reconnect
          // screen, instead of silently retrying for MAX_HUNG_CONNECT_RETRIES attempts (~75s)
          // with no UI at all (showSelectDevicePopup() deliberately hides the generic
          // reconnect popup for the whole OTA).
          if (this.updateInProgress && !this.expectingReboot && !this.otaVerifying) {
            this.otaReconnectStuckFailures++
            if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG mid-upload reconnect attempt hung past ${OUTER_TIMEOUT_MS}ms (failure ${this.otaReconnectStuckFailures}/2) for ${this.deviceName ?? this.deviceId}`)
            this.lastSelfCancelAt = Date.now()
            BleClient.enableQueue()
            if (this.otaReconnectStuckFailures < 2) {
              setTimeout(() => this.connect(maxAttempts), 1000)
            } else {
              void this.teardownStuckOtaConnection()
            }
            return
          }

          this.hungConnectRetries++
          console.warn(`[FWUpdate] state=RECONNECTING: GATT connect attempt for ${this.deviceName ?? this.deviceId} hung past ${OUTER_TIMEOUT_MS}ms (BleClient queue stalled before its own timeout) — resetting BLE plugin queue (attempt ${this.hungConnectRetries}/${PhysensBleDevice.MAX_HUNG_CONNECT_RETRIES})`)
          this.lastSelfCancelAt = Date.now()
          BleClient.enableQueue()
          if (this.autoReconnectPausedForVerify) {
            console.log(`[BLE][${this.deviceName ?? this.deviceId}] hung-connect retry suppressed — auto-reconnect paused for verify`)
          } else if (this.hungConnectRetries <= PhysensBleDevice.MAX_HUNG_CONNECT_RETRIES) {
            setTimeout(() => this.connect(maxAttempts), 1000)
          } else {
            console.error(`[FWUpdate] state=RECONNECTING: giving up after ${PhysensBleDevice.MAX_HUNG_CONNECT_RETRIES} hung connect attempts for ${this.deviceName ?? this.deviceId} — prompting user to reselect`)
            this.reconnectRequiresUserReselect.next(true)
          }
        }, OUTER_TIMEOUT_MS)
        // Lets pauseAutoReconnectForVerify() abandon this in-flight connect: marks it settled
        // (so the late resolve/reject below ignores itself) and kills the retry timer/heartbeat.
        this.pendingConnectCleanup = () => {
          outerSettled = true
          clearTimeout(outerTimer)
          clearInterval(diagHeartbeat)
        }

        const connectStartedAt = Date.now()
        // When simulating, never resolve/reject — let the shrunk OUTER_TIMEOUT_MS above fire
        // and drive the OTA branch, without ever touching the real BleClient/BLE stack.
        const connectPromise = simulateStuckReconnect
          ? new Promise<void>(() => {})
          : BleClient.connect(HexStringUtils.hexToString(this.deviceId), ()=> {this.updateDeviceState(Device_State.DISCONNECTED)}, connectOptions)
        connectPromise.then(()=>{
          if (outerSettled) { console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect resolved after outer timeout already retried; ignoring stale result`); return }
          outerSettled = true
          clearTimeout(outerTimer)
          clearInterval(diagHeartbeat)
          this.pendingConnectCleanup = null
          if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG BleClient.connect() RESOLVED in ${Date.now() - connectStartedAt}ms for ${this.deviceName ?? this.deviceId} at ${new Date().toISOString()} (updateInProgress=${this.updateInProgress})`)
          this.hungConnectRetries = 0
          this.otaReconnectStuckFailures = 0
          this.connectInProgress = false
          // The user (or app) may have asked to disconnect while this connect attempt
          // was in flight — honor that instead of resurrecting the link.
          if (this.removed || this.userInitiatedDisconnect) {
            console.log(`[BLE][${this.deviceName ?? this.deviceId}] connect resolved after user-initiated disconnect; tearing back down`)
            BleClient.disconnect(HexStringUtils.hexToString(this.deviceId)).catch(() => {})
            return
          }
          this.hasConnected = true;
          if (this.disconnectedAtMs !== null) {
            const durationMs = Date.now() - this.disconnectedAtMs
            const pathLabel = this.reconnectMethod === 'manual reselect' ? 'reselect' : 'auto'
            console.log(`[BLE][${this.deviceName ?? this.deviceId}] reconnect method=${this.reconnectMethod ?? 'unknown'} path=${pathLabel} duration=${durationMs}ms`)
          }
          this.reconnectMethod = null
          this.reconnectDelayMs = 3000
          this.disconnectedAtMs = null
          this.clearReselectTimer()
          this.reconnectRequiresUserReselect.next(false)
          console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect success; backoff reset to ${this.reconnectDelayMs}ms`)
          this.updateDeviceState(Device_State.CONNECTED)
        }, (err)=> {
          if (outerSettled) { console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect rejected after outer timeout already retried; ignoring stale result`); return }
          outerSettled = true
          clearTimeout(outerTimer)
          clearInterval(diagHeartbeat)
          this.pendingConnectCleanup = null
          this.connectInProgress = false
          if (OTA_DIAG) {
            const errName = (err as any)?.name ?? 'UnknownError'
            const sinceSelfCancelMs = this.lastSelfCancelAt !== null ? Date.now() - this.lastSelfCancelAt : null
            // An AbortError within ~3s of our own enableQueue()/cleanup/disconnect is our own
            // cancel/retry loop killing the in-flight connect, NOT a device-side link failure.
            const selfInflicted = errName === 'AbortError' && sinceSelfCancelMs !== null && sinceSelfCancelMs < 3000
            console.warn(`[FWUpdate][DIAG] BleClient.connect() REJECTED in ${Date.now() - connectStartedAt}ms for ${this.deviceName ?? this.deviceId} name=${errName}${errName === 'AbortError' ? ` (selfInflicted=${selfInflicted}, ${sinceSelfCancelMs ?? '?'}ms since our last self-cancel)` : ''}`)
          }
          console.warn(`[BLE][${this.deviceName ?? this.deviceId}] connect failed`, err)

          // Mirrors the outer-timeout OTA branch above: a genuine GATT-level rejection
          // (not a hang) during an ACTIVE mid-upload OTA gets the same isolated 2-strikes
          // counter and teardown, never touching the non-OTA/post-reboot-window/verify-window
          // logic below.
          if (this.updateInProgress && !this.expectingReboot && !this.otaVerifying) {
            this.otaReconnectStuckFailures++
            if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA_DIAG mid-upload reconnect attempt rejected (failure ${this.otaReconnectStuckFailures}/2) for ${this.deviceName ?? this.deviceId}`)
            if (this.otaReconnectStuckFailures >= 2) {
              void this.teardownStuckOtaConnection()
              return
            }
          }

          // Only meaningful for an actual reconnect (disconnectedAtMs set), not the very
          // first connect() a new device instance ever makes.
          if (this.disconnectedAtMs !== null && String((err as any)?.message ?? err).includes('no longer in range')) {
            this.staleAddressReconnectFailureSubject.next()
          }

          // Firmware updates deliberately reboot the device (cmdReset) and expect the
          // disconnect to outlast reselectThresholdMs — that reboot/re-advertise cycle is
          // owned by firmwareupdate.component's own waitForDeviceReboot() (up to 120s).
          // Tripping "needs reselect" mid-update pops the Reconnect Device dialog before
          // the update finishes, and (via scheduleReconnect's guard) stops the very
          // background retries waitForDeviceReboot is waiting on.
          if (Capacitor.getPlatform() === 'web' && this.disconnectedAtMs !== null && !this.expectingReboot) {
            const elapsedMs = Date.now() - this.disconnectedAtMs
            console.debug(`[BLE][${this.deviceName ?? this.deviceId}] web reconnect elapsed ${elapsedMs}ms / threshold ${this.reselectThresholdMs}ms`)
            if (elapsedMs >= this.reselectThresholdMs && !this.reconnectRequiresUserReselect.value) {
              console.warn(`[BLE][${this.deviceName ?? this.deviceId}] web reconnect timed out after ${elapsedMs}ms — user reselect required`)
              this.reconnectRequiresUserReselect.next(true)
            }
          }

          this.updateDeviceState(Device_State.DISCONNECTED)
        })
        return true;
      }
      console.debug(`[BLE][${this.deviceName ?? this.deviceId}] connect blocked by attempt limit`, {
        attempts: this.connectionAttempts,
        maxAttempts: this.maxConnectionAttempts,
      })
      return false;
    }
    
    initMTUSize() {
        // try {
        //   BleClient.getMtu(HexStringUtils.hexToString(this.deviceId)).then((value)=>{
        //     this.mtuLength = value - 4
        //     console.log("new mtu length: ", this.mtuLength)
      
        //   })
        // } catch(e) {
        //   console.log("Error getting mtu size: ", e)
        // }
        
      }
    
      setupNotifications() {
        console.log("setting up notifications")
        BleClient.startNotifications(
          HexStringUtils.hexToString(this.deviceId), 
          GATT_UUID.nusServiceGUID(0x00), 
          GATT_UUID.nusTxCharactGUID(0x00), 
          (data:DataView)=> {this.rxBytes(data)}
        ).then(
          ()=>{this.updateDeviceState(Device_State.READY_FOR_PACKETS)}, 
          ()=>{console.log("Failed to set up notifications")}
        )
    
        BleClient.startNotifications(
          HexStringUtils.hexToString(this.deviceId),
          McumgrCap.SERVICE_UUID,
          McumgrCap.CHARACTERISTIC_UUID,
          (data: DataView)=>{this.mcumgr._notification(data)}
        ).then(
          ()=>{console.log("Successfully set up notifications for mcumgr")},
          ()=>{console.warn("Failed to set up notifications for mcumgr")},
        )
        // BleClient.startNotifications(HexStringUtils.hexToString(this.deviceId), GATT_UUID.devStateServiceGUID(0x00), GATT_UUID.devStateBatteryStateGUID(0x00),  (data:DataView)=>{this.batteryStateNotificationCallback(data)}).then(()=>{console.log("Battery State char setup")}, (e)=>{console.error(e);console.log("Failed to set up battery state notification")})
    
        // BleClient.startNotifications(HexStringUtils.hexToString(this.deviceId), McumgrCap.SERVICE_UUID, McumgrCap.CHARACTERISTIC_UUID, (data: DataView)=>this.mcumgr._notification(data))
        // BleClient.startNotifications(HexStringUtils.hexToString(this.deviceId), GATT_UUID.devStateServiceGUID(), GATT_UUID.devStateBatteryStateGUID(), (data:DataView)=>{
        //   this.battery_state = data.getUint8(0)
        // })
    
        // BleClient.startNotifications(HexStringUtils.hexToString(this.deviceId), GATT_UUID.devStateServiceGUID(), GATT_UUID.devStateRadioStateGUID(), (data:DataView)=>{
        //   this.radio_state = data.getUint8(0)
        // })
    
      }

      override updateDeviceState(state: Device_State): void {
        super.updateDeviceState(state)
        switch(this.deviceState) {
            case Device_State.CONNECTED:
                if (OTA_DIAG) console.log(`[FWUpdate][DIAG] link CONNECTED at ${new Date().toISOString()} for ${this.deviceName ?? this.deviceId} (updateInProgress=${this.updateInProgress})`)
                this.setupNotifications()

                break;
            case Device_State.READY_FOR_PACKETS:
                this.initMTUSize()
                this.startIntervalSubscriptions()
                // Connect-time housekeeping shares the BLE write queue with the mcumgr
                // OTA chunk writes — skip it while an update is in progress so it can't
                // strand a packet behind updateInProgress's gate in clearPacketBuffer().
                if(!this.updateInProgress) {
                  this.getDate()
                  if(this.publishers.length == 0) {
                  this.getPublishers()
                  }
                  if(this.parameterSets.length == 0) {
                  this.setAccessControlRole(ACCESS_CONTROL_ROLE.FACTORY, "IamIDLPhysensFactory")
                  this.getParameterSetList()
                  }
                  this.getDevInfo()
                }
                break;
            case Device_State.DISCONNECTED:
              console.log(`[BLE][${this.deviceName ?? this.deviceId}] disconnect reason=${this.userInitiatedDisconnect ? 'user' : 'link-loss'}`)
              if (OTA_DIAG) {
                const now = Date.now()
                const sinceSent = this.lastOtaKeepaliveSentAt !== null ? ((now - this.lastOtaKeepaliveSentAt) / 1000).toFixed(1) : 'n/a'
                const sinceAck = this.lastOtaKeepaliveAckAt !== null ? ((now - this.lastOtaKeepaliveAckAt) / 1000).toFixed(1) : 'n/a'
                console.log(`[FWUpdate][DIAG] link DISCONNECTED at ${new Date().toISOString()} for ${this.deviceName ?? this.deviceId} reason=${this.userInitiatedDisconnect ? 'user' : 'link-loss'} (updateInProgress=${this.updateInProgress}) seconds since last OTA keepalive sent=${sinceSent}s received=${sinceAck}s`)
              }
                if (this.disconnectedAtMs === null) {
                  this.disconnectedAtMs = Date.now()
                  console.log(`[BLE][${this.deviceName ?? this.deviceId}] disconnected at ${new Date(this.disconnectedAtMs).toISOString()}; last recorder stats at ${this.lastFileRecorderStatsTime ? new Date(this.lastFileRecorderStatsTime).toISOString() : 'never'}`)
                }
                this.keepAliveSubscription?.unsubscribe()
                this.updateTimeIntervalSubscription?.unsubscribe()
                this.connectionStatsIntervalSubscription?.unsubscribe()
                this.clearPendingWrites('Device disconnected')
                this.mcumgr.clearPendingResolvers('Device disconnected')
                // A write in flight when the link drops can leave writePromise
                // permanently unsettled (BleClient.write*() against a torn-down GATT
                // server has been observed to hang rather than reject) - clearing it
                // here is the only thing that keeps acquireBleWriteSlot()'s mutex from
                // wedging every future write (including the reconnect's own mcumgr
                // commands) for the rest of the session.
                this.writePromise = null
                if (this.userInitiatedDisconnect) {
                  console.log(`[BLE][${this.deviceName ?? this.deviceId}] reconnect cancelled (user-initiated disconnect)`)
                } else {
                  this.armReselectTimer()
                  this.scheduleReconnect()
                }
                break;
                // this.connect()
        }
    }

    override txBytes(bytes: DataView): Promise<void> {
        return BleClient.write(HexStringUtils.hexToString(this.deviceId), GATT_UUID.nusServiceGUID(0x00), GATT_UUID.nusRxCharactGUID(0x00), bytes)
    }

    override async canTxBytes(): Promise<boolean> {
        return (await BleClient.isEnabled() == true)
    }

    override remove() {
      // remove() is how every manual disconnect path in this app (Disconnect button,
      // charging/inactivity auto-disconnect) actually tears a device down — mark it as
      // user-initiated so scheduleReconnect()/updateDeviceState() never resurrect it,
      // and abort any in-flight connect/reconnect activity for good measure.
      this.userInitiatedDisconnect = true
      this.clearReconnectTimer()
      this.clearReselectTimer()
      this.connectInProgress = false
      this.disconnectedAtMs = null
      this.reconnectRequiresUserReselect.next(false)
      console.log(`[BLE][${this.deviceName ?? this.deviceId}] disconnect reason=user (remove); reconnect disabled`)
      this.mcumgr.remove()
      super.remove();
      // Ensure the physical GATT link is torn down even if no in-flight connect/disconnect
      // callback was going to do it — a manual disconnect must not leave the link lingering.
      BleClient.disconnect(HexStringUtils.hexToString(this.deviceId)).catch(() => {})
    }
}

